import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Link,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import api from '../config/api';
import PaymentWall from './PaymentWall';
import PartnerCapacityDialog from './PartnerCapacityDialog';
import usePremiumAI from '../hooks/usePremiumAI';
import { DOCUMENT } from '../constants/documentTheme';
import { partnerOxbloodBtnSx, partnerOutlineBtnSx } from './PartnerListChrome';
import LazyPlacePhoto from './LazyPlacePhoto';
import AiSuggestionPoiMap from './AiSuggestionPoiMap';
import PartnerAiSearchingPanel from './PartnerAiSearchingPanel';
import { appendAiItineraryToTrip, hasAiItinerarySelections } from '../utils/appendAiItinerary';
import { isManagedByPartner, partnerTripIncludesAiPlanning, showPaidAiBadge } from '../utils/tripUtils';
import { aiGateMessage, isPartnerPlanLimitError, partnerCapacityFromError } from '../utils/partnerPlan';
import { PaidFeatureLabel } from './DocumentProBadge';
import { knownCityCoords } from '../utils/knownCityCoordinates';
import {
  attachNearbyPlacesToPicks,
  collectTripAnchorPoints,
  filterNearbyByCategory,
  filterPicksNearTripAnchors,
  formatNearbyCatalog,
  loadNearbyPlacesForTrip,
  priceLevelLabel,
} from '../utils/aiSuggestionPlaces';
import {
  aiPickGroupKey,
  aiPickKindKey,
  buildPartnerTripAiBrief,
  buildPartnerTripAiPreferences,
  defaultClientTier,
  flattenAiPicks,
  groupAiPicks,
  isHttpUrl,
  parseAiRating,
  parseHotelStarClass,
  aiPickBookingUrl,
  aiPickGoogleUrl,
  aiPickWebsiteUrl,
  aiPickSummary,
  selectedPicksToItinerary,
  AI_SUGGESTION_RADIUS_MILES,
  dayNumbersFromCreated,
  tripLocationNames,
} from '../utils/partnerTripAiContext';

const PartnerTripAIDrawer = ({ open, onClose, trip, onAdded, usePartnerAuth = true, seedPrompt = '' }) => {
  const { t } = useTranslation();
  const [extraContext, setExtraContext] = useState('');
  const [clientTier, setClientTier] = useState(defaultClientTier(trip));
  const [itinerary, setItinerary] = useState([]);
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showCapacityDialog, setShowCapacityDialog] = useState(false);
  const [limitError, setLimitError] = useState(null);
  const [pendingGenerate, setPendingGenerate] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const [detailPick, setDetailPick] = useState(null);
  const [hoveredPickKey, setHoveredPickKey] = useState(null);
  const [nearbyByCategory, setNearbyByCategory] = useState({
    hotels: [],
    restaurants: [],
    locations: [],
    activities: []
  });
  const [anchorExtras, setAnchorExtras] = useState({});
  const [partnerEntitlements, setPartnerEntitlements] = useState(null);

  useEffect(() => {
    if (!open) {
      setShowPaywall(false);
      setShowCapacityDialog(false);
      setLimitError(null);
      setPendingGenerate(false);
      setPartnerEntitlements(null);
      return;
    }
    setExtraContext('');
    setHoveredPickKey(null);
    setClientTier(defaultClientTier(trip));
    setItinerary([]);
    setAnchorExtras({});
    // Fresh form each time the drawer opens or the partner switches trips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, trip?.id]);

  const destinationNames = useMemo(
    () => tripLocationNames(trip),
    [trip]
  );

  const anchors = useMemo(
    () => collectTripAnchorPoints({ trip, extraByName: anchorExtras }),
    [trip, anchorExtras]
  );

  const nearbyInRadius = useMemo(
    () => filterNearbyByCategory(nearbyByCategory, anchors),
    [nearbyByCategory, anchors]
  );

  const nearbyCatalog = useMemo(
    () => formatNearbyCatalog(nearbyInRadius),
    [nearbyInRadius]
  );

  // Auto trip summary stays behind the scenes; optional advisor notes are appended.
  const preferences = useMemo(() => {
    const destinationCoords = anchors.map((point) => ({
      name: point.name,
      lat: point.lat,
      lng: point.lng
    }));
    const tripSummary = buildPartnerTripAiBrief({
      trip,
      itinerary,
      clientTier,
      nearbyCatalog,
      destinationCoords
    });
    return buildPartnerTripAiPreferences({
      trip,
      itinerary,
      brief: tripSummary,
      extraContext,
      seedPrompt,
      clientTier,
      nearbyCatalog,
      destinationCoords
    });
  }, [trip, itinerary, extraContext, seedPrompt, clientTier, nearbyCatalog, anchors]);

  const {
    recommendations,
    isGenerating,
    searchProgress,
    error,
    generateRecommendations,
    selectOption,
    selectedOptions
  } = usePremiumAI(preferences, { tripId: trip?.id, usePartnerAuth: true });

  const grantAccess = () => {
    setHasAccess(true);
    setShowPaywall(false);
  };

  const handlePartnerAiLimitError = (err) => {
    if (!isPartnerPlanLimitError(err)) {
      enqueueSnackbar(aiGateMessage(err, t), {
        variant: 'error',
        autoHideDuration: 6000
      });
      return;
    }
    const capacity = partnerCapacityFromError(err);
    setLimitError(capacity);
    if (capacity?.code === 'partner_ai_paywall') {
      setPendingGenerate(true);
      setShowPaywall(true);
      return;
    }
    if (capacity?.code === 'partner_ai_limit') {
      setShowCapacityDialog(true);
    }
  };

  useEffect(() => {
    if (!open || !trip?.id) return undefined;
    let cancelled = false;
    const loadItinerary = async () => {
      try {
        const response = await api.get(
          `/trips/${trip.id}/itinerary?t=${Date.now()}`,
          usePartnerAuth ? { usePartnerAuth: true } : undefined
        );
        if (!cancelled) setItinerary(Array.isArray(response.data) ? response.data : []);
      } catch (loadError) {
        if (!cancelled) setItinerary([]);
      }
    };
    loadItinerary();
    return () => { cancelled = true; };
  }, [open, trip?.id, usePartnerAuth]);

  useEffect(() => {
    if (!open || !destinationNames.length) return undefined;
    let cancelled = false;
    const resolveAnchors = async () => {
      const extras = {};
      await Promise.all(destinationNames.map(async (name) => {
        const known = knownCityCoords(name);
        if (known) {
          extras[name] = known;
          return;
        }
        try {
          const response = await api.get('/geocoding', { params: { city: name } });
          const coords = response.data?.coordinates || response.data;
          const lat = Number(coords?.lat);
          const lng = Number(coords?.lng);
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            extras[name] = { lat, lng };
          }
        } catch {
          // Geocoding is optional; name matching still keeps obvious local picks.
        }
      }));
      if (!cancelled) setAnchorExtras(extras);
    };
    resolveAnchors();
    return () => { cancelled = true; };
  }, [open, destinationNames]);

  useEffect(() => {
    if (!open || !trip?.id) return undefined;
    let cancelled = false;
    const destinations = destinationNames;
    const requestConfig = usePartnerAuth ? { usePartnerAuth: true } : undefined;
    loadNearbyPlacesForTrip({ tripId: trip.id, destinations, requestConfig })
      .then((result) => {
        if (!cancelled) setNearbyByCategory(result.byCategory);
      })
      .catch(() => {
        if (!cancelled) {
          setNearbyByCategory({ hotels: [], restaurants: [], locations: [], activities: [] });
        }
      });
    return () => { cancelled = true; };
  }, [open, trip?.id, destinationNames, usePartnerAuth]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const checkAccess = async () => {
      setCheckingAccess(true);
      if (usePartnerAuth) {
        try {
          const planRes = await api.get('/partners/plan', { usePartnerAuth: true });
          const entitlements = planRes.data?.entitlements;
          if (!cancelled) setPartnerEntitlements(entitlements || null);
          if (entitlements?.features?.hasAiPlanning) {
            try {
              await api.get('/partners/ai-access', { usePartnerAuth: true });
              if (!cancelled) grantAccess();
              return;
            } catch (allowanceError) {
              if (!cancelled) {
                setHasAccess(false);
                setShowPaywall(false);
                if (isPartnerPlanLimitError(allowanceError)) {
                  const capacity = partnerCapacityFromError(allowanceError);
                  setLimitError(capacity);
                  if (capacity?.code === 'partner_ai_paywall') {
                    setShowPaywall(true);
                  } else if (capacity?.code === 'partner_ai_limit') {
                    setShowCapacityDialog(true);
                  }
                }
              }
              return;
            }
          }
        } catch (planError) {
          console.error('Partner plan check for AI:', planError);
        }
      }

      if (partnerTripIncludesAiPlanning(trip)) {
        if (!cancelled) grantAccess();
        return;
      }

      try {
        const response = await api.get(`/payments/check-ai-concierge-access?tripId=${trip.id}`);
        if (!cancelled) {
          setHasAccess(Boolean(response.data?.hasAccess));
          setShowPaywall(false);
        }
      } catch (accessError) {
        if (!cancelled) {
          setHasAccess(false);
          setShowPaywall(false);
        }
      } finally {
        if (!cancelled) setCheckingAccess(false);
      }
    };
    checkAccess().finally(() => {
      if (!cancelled) setCheckingAccess(false);
    });
    return () => { cancelled = true; };
  }, [open, trip, usePartnerAuth]);

  const managedTrip = isManagedByPartner(trip);
  const addAsDrafts = managedTrip || usePartnerAuth;

  const refreshAccess = async () => {
    if (partnerTripIncludesAiPlanning(trip)) {
      grantAccess();
      return true;
    }
    const response = await api.get(`/payments/check-ai-concierge-access?tripId=${trip.id}`);
    const allowed = Boolean(response.data?.hasAccess);
    setHasAccess(allowed);
    setShowPaywall(false);
    return allowed;
  };

  const handleGenerate = async () => {
    if (checkingAccess || isGenerating) return;
    if (!hasAccess) {
      setPendingGenerate(true);
      setShowPaywall(true);
      return;
    }

    if (usePartnerAuth) {
      try {
        await api.post(
          '/partners/ai-usage',
          { tripId: trip?.id, requests: 1, preflight: true },
          { usePartnerAuth: true }
        );
      } catch (err) {
        handlePartnerAiLimitError(err);
        return;
      }
    }

    let result;
    try {
      result = await generateRecommendations();
    } catch (err) {
      enqueueSnackbar(aiGateMessage(err, t), { variant: 'error', autoHideDuration: 6000 });
      return;
    }
    if (!usePartnerAuth || !result) return;

    // The suggestions are already on screen, so this is bookkeeping only:
    // report a plan limit, but never fail the generation the partner just saw.
    try {
      const aiUsage = result.aiUsage || {};
      await api.post(
        '/partners/ai-usage',
        {
          tripId: trip?.id,
          requests: 1,
          tokens: aiUsage.tokens || 0,
          modelCost: aiUsage.modelCost || 0,
          model: aiUsage.model
        },
        { usePartnerAuth: true }
      );
    } catch (err) {
      if (isPartnerPlanLimitError(err)) {
        handlePartnerAiLimitError(err);
        return;
      }
      console.error('Partner AI usage was not recorded:', err);
    }
  };

  const picks = useMemo(
    () => filterPicksNearTripAnchors(
      attachNearbyPlacesToPicks(flattenAiPicks(recommendations), nearbyInRadius),
      anchors
    ),
    [recommendations, nearbyInRadius, anchors]
  );
  const pickGroups = groupAiPicks(picks);
  const existingStopCount = itinerary.filter((item) => item?.name).length;
  const destinationLabel = (preferences.destinations || []).join(', ');

  const handleAdd = async () => {
    const selected = selectedPicksToItinerary(picks, selectedOptions);
    if (!hasAiItinerarySelections(selected)) {
      setAddError(t('partnerAi.pickRequired'));
      return;
    }
    try {
      setAdding(true);
      setAddError('');
      const created = await appendAiItineraryToTrip(
        api,
        trip.id,
        selected,
        trip.start_date,
        trip.end_date,
        {
          clientVisible: !addAsDrafts,
          requestConfig: usePartnerAuth ? { usePartnerAuth: true } : undefined
        }
      );
      const days = dayNumbersFromCreated(created, trip.start_date);
      enqueueSnackbar(
        days.length
          ? t(addAsDrafts ? 'partnerAi.addedDraftsOnDay' : 'partnerAi.addedVisibleOnDay', { count: created.length, days: days.join(', ') })
          : t(addAsDrafts ? 'partnerAi.addedDrafts' : 'partnerAi.addedVisible', { count: created.length }),
        {
          variant: 'success',
          autoHideDuration: 7000
        }
      );
      onAdded?.(created);
      onClose?.();
    } catch (err) {
      setAddError(err.response?.data?.error || t('partnerAi.addFailed'));
    } finally {
      setAdding(false);
    }
  };

  const showGeneratePaidBadge = showPaidAiBadge(trip, { entitlements: partnerEntitlements }) && !hasAccess;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        'data-testid': 'partner-trip-ai-drawer',
        sx: {
          width: { xs: '100%', sm: 640, md: 760 },
          maxWidth: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          overflow: 'hidden'
        }
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          px: { xs: 2, md: 3.5 },
          pt: { xs: 'calc(12px + env(safe-area-inset-top, 0px))', md: 3 },
          pb: 1.5,
          borderBottom: `1px solid ${DOCUMENT.ruleHard}`,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1, pt: 0.5, pl: { xs: 1, md: 0.5 } }}>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 26 }}>
            {t('partnerAi.title')}
          </Typography>
          <Typography sx={{ fontSize: 14, color: DOCUMENT.ink3, mt: 1 }}>
            {t(addAsDrafts ? 'partnerAi.subtitle' : 'partnerAi.subtitleTraveler')}
          </Typography>
        </Box>
        <IconButton
          aria-label={t('common.close')}
          onClick={onClose}
          data-testid="partner-trip-ai-close"
          sx={{ color: DOCUMENT.ink, mt: 0.25 }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 3, md: 4 }, pt: 2.5, pb: 2 }}>

      {checkingAccess ? (
        <CircularProgress size={24} sx={{ mb: 2 }} />
      ) : null}

      <Box>
          <Box
            data-testid="partner-trip-ai-context"
            sx={{
              mb: 2.5,
              p: 2,
              border: `1px solid ${DOCUMENT.ruleHard}`,
              bgcolor: DOCUMENT.canvas
            }}
          >
            <Typography sx={{ fontSize: 11, letterSpacing: '0.11em', textTransform: 'uppercase', color: DOCUMENT.ink3, mb: 0.75 }}>
              {t('partnerAi.contextHeading')}
            </Typography>
            <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2 }}>
              {[
                destinationLabel,
                trip?.start_date && trip?.end_date && `${trip.start_date} – ${trip.end_date}`,
                trip?.trip_type
              ].filter(Boolean).join(' · ')}
            </Typography>
            <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mt: 0.75 }}>
              {existingStopCount > 0
                ? t('partnerAi.contextStops', { count: existingStopCount })
                : t('partnerAi.contextEmptyItinerary')}
            </Typography>
            {destinationLabel ? (
              <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mt: 0.75 }}>
                {t('partnerAi.vicinityNote', {
                  places: destinationLabel,
                  miles: AI_SUGGESTION_RADIUS_MILES
                })}
              </Typography>
            ) : null}
          </Box>

          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mb: 1 }}>
            {t('partnerAi.tierLabel')}
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={clientTier}
            onChange={(_, value) => { if (value) setClientTier(value); }}
            sx={{ mb: 1, '& .MuiToggleButton-root': { textTransform: 'none', py: 1 } }}
            data-testid="partner-trip-ai-tier"
          >
            <ToggleButton value="highend">{t('partnerAi.tierHighend')}</ToggleButton>
            <ToggleButton value="mid">{t('partnerAi.tierMid')}</ToggleButton>
            <ToggleButton value="budget">{t('partnerAi.tierBudget')}</ToggleButton>
          </ToggleButtonGroup>
          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 2.5 }}>
            {t('partnerAi.tierHint')}
          </Typography>

          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            label={t('partnerAi.extraContext')}
            helperText={t('partnerAi.extraContextHint')}
            placeholder={t('partnerAi.extraContextPlaceholder')}
            value={extraContext}
            onChange={(event) => setExtraContext(event.target.value)}
            inputProps={{
              'data-testid': 'partner-trip-ai-extra-context',
            }}
            sx={{
              mb: 2,
              '& .MuiInputBase-input': {
                fontSize: 15,
                lineHeight: 1.5,
              },
            }}
          />
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>}
          {isGenerating ? (
            <PartnerAiSearchingPanel
              destinationLabel={destinationLabel}
              progress={searchProgress}
            />
          ) : null}
          {hasAccess && !isGenerating && pickGroups.length > 0 && (
            <Box data-testid="partner-trip-ai-picks" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 2.5 }}>
              <Box
                data-testid="partner-trip-ai-map-pin"
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 4,
                  mb: 2.5,
                  pt: 0.5,
                  pb: 1.25,
                  bgcolor: DOCUMENT.surface,
                  boxShadow: `0 12px 18px -10px ${DOCUMENT.surface}`
                }}
              >
                <AiSuggestionPoiMap
                  picks={picks}
                  destinations={preferences.destinations || []}
                  hoveredPickKey={hoveredPickKey}
                />
              </Box>
              {pickGroups.map((group) => (
                <Box key={group.category} data-testid={`partner-trip-ai-group-${group.category}`}>
                  <Typography
                    sx={{
                      fontSize: 11,
                      letterSpacing: '0.11em',
                      textTransform: 'uppercase',
                      color: DOCUMENT.ink3,
                      mb: 1
                    }}
                  >
                    {t(`partnerAi.${aiPickGroupKey(group.category)}`)}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {group.picks.map((pick) => {
                      const selected = (selectedOptions[pick.category]?.[pick.dayIndex] || []).includes(pick.optionIndex);
                      return (
                        <Box
                          key={pick.key}
                          data-testid={`partner-trip-ai-pick-row-${pick.key}`}
                          onMouseEnter={() => setHoveredPickKey(pick.key)}
                          onMouseLeave={() => setHoveredPickKey((current) => (
                            current === pick.key ? null : current
                          ))}
                          sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}
                        >
                          <Checkbox
                            checked={selected}
                            onChange={() => selectOption(pick.category, pick.dayIndex, pick.optionIndex)}
                            inputProps={{ 'aria-label': pick.label }}
                            sx={{ mt: -0.5 }}
                          />
                          {pick.item?.photoName && trip?.id ? (
                            <LazyPlacePhoto
                              tripId={trip.id}
                              photoName={pick.item.photoName}
                              alt={pick.label}
                              width={72}
                              height={54}
                              usePartnerAuth={usePartnerAuth}
                              fallback={<Box sx={{ width: 72, height: 54, bgcolor: DOCUMENT.canvas }} />}
                            />
                          ) : null}
                          <Box sx={{ minWidth: 0, pt: 0.5, flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                              <Typography
                                component="button"
                                type="button"
                                onClick={() => setDetailPick(pick)}
                                data-testid={`partner-trip-ai-pick-${pick.category}-${pick.optionIndex}`}
                                sx={{
                                  border: 0,
                                  background: 'none',
                                  p: 0,
                                  m: 0,
                                  cursor: 'pointer',
                                  textAlign: 'left',
                                  fontSize: 15,
                                  lineHeight: 1.4,
                                  color: DOCUMENT.ink,
                                  fontFamily: DOCUMENT.sans,
                                  '&:hover': {
                                    color: DOCUMENT.oxblood,
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                {pick.label}
                              </Typography>
                              <Chip
                                size="small"
                                label={t(`partnerAi.${aiPickKindKey(pick.category)}`)}
                                sx={{
                                  height: 22,
                                  fontSize: 11,
                                  letterSpacing: '0.04em',
                                  borderRadius: 0,
                                  bgcolor: DOCUMENT.canvas,
                                  border: `1px solid ${DOCUMENT.ruleHard}`,
                                  color: DOCUMENT.ink3
                                }}
                              />
                            </Box>
                            <AiPickListMeta pick={pick} />
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
      </Box>
      </Box>
      <Box
        data-testid="partner-trip-ai-actions"
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          px: { xs: 3, md: 4 },
          py: 2,
          borderTop: `1px solid ${DOCUMENT.ruleHard}`,
          bgcolor: DOCUMENT.surface
        }}
      >
        <Button
          onClick={handleGenerate}
          disabled={isGenerating || checkingAccess}
          sx={{ ...partnerOxbloodBtnSx, border: '1px solid transparent' }}
          startIcon={isGenerating ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
        >
          <PaidFeatureLabel show={showGeneratePaidBadge}>
            {isGenerating ? t('partnerAi.generating') : t('partnerAi.generate')}
          </PaidFeatureLabel>
        </Button>
        <Button
          onClick={handleAdd}
          disabled={adding || !hasAccess || !picks.length}
          sx={partnerOutlineBtnSx}
        >
          {adding ? t('partnerAi.adding') : t(addAsDrafts ? 'partnerAi.addSelected' : 'partnerAi.addSelectedVisible')}
        </Button>
      </Box>
      <PaymentWall
        open={showPaywall}
        onClose={() => {
          setShowPaywall(false);
          setPendingGenerate(false);
        }}
        tripId={trip?.id}
        tripTitle={t('plans.ai_concierge.name')}
        isOwner
        tierType="ai_concierge"
        onSuccess={() => {
          refreshAccess()
            .then((allowed) => {
              if (allowed && pendingGenerate) {
                setPendingGenerate(false);
                handleGenerate();
              }
            })
            .catch(() => {
              setHasAccess(false);
              setShowPaywall(true);
            });
        }}
      />
      <PartnerCapacityDialog
        open={showCapacityDialog}
        onClose={() => {
          setShowCapacityDialog(false);
          setLimitError(null);
        }}
        limitError={limitError}
        onSuccess={() => {
          setShowCapacityDialog(false);
          setLimitError(null);
          grantAccess();
        }}
      />
      <AiPickDetailDialog
        pick={detailPick}
        tripId={trip?.id}
        usePartnerAuth={usePartnerAuth}
        onClose={() => setDetailPick(null)}
      />
    </Drawer>
  );
};

function AiPickListMeta({ pick }) {
  const { t } = useTranslation();
  const item = pick?.item || {};
  const review = parseAiRating(item.rating);
  const hotelStars = parseHotelStarClass(item.starRating);
  const googleUrl = aiPickGoogleUrl(item, { name: pick?.label, city: pick?.city });
  const summary = aiPickSummary(item);
  const price = priceLevelLabel(item.priceLevel) || item.priceRange || item.estimatedCost;
  const facts = [
    hotelStars && t('partnerAi.hotelStars', { count: hotelStars }),
    review && t('partnerAi.reviewStars', { rating: Number(review).toFixed(1) }),
    item.userRatingCount && t('partnerAi.reviewsCount', { count: item.userRatingCount }),
    price,
    pick?.city,
    item.nearbyMatch && t('partnerAi.nearbyBadge')
  ].filter(Boolean);

  if (!facts.length && !googleUrl && !summary) return null;

  return (
    <Box sx={{ mt: 0.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, columnGap: 1.25 }}>
        {facts.length ? (
          <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3 }}>
            {facts.join(' · ')}
          </Typography>
        ) : null}
        {googleUrl ? (
          <Link
            href={googleUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`partner-trip-ai-google-${pick.category}-${pick.optionIndex}`}
            onClick={(event) => event.stopPropagation()}
            sx={{ fontSize: 12, color: DOCUMENT.oxblood, textDecoration: 'underline' }}
          >
            {t('partnerAi.google')}
          </Link>
        ) : null}
      </Box>
      {summary ? (
        <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 0.25 }}>
          {summary}
        </Typography>
      ) : null}
    </Box>
  );
}

function DetailField({ label, children }) {
  if (children == null || children === '') return null;
  return (
    <Box sx={{ mb: 1.75 }}>
      <Typography
        sx={{
          fontSize: 11,
          letterSpacing: '0.11em',
          textTransform: 'uppercase',
          color: DOCUMENT.ink3,
          mb: 0.5
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2, whiteSpace: 'pre-wrap' }}>
        {children}
      </Typography>
    </Box>
  );
}

function AiPickDetailDialog({ pick, onClose, tripId, usePartnerAuth = false }) {
  const { t } = useTranslation();
  const item = pick?.item || {};
  const cost = priceLevelLabel(item.priceLevel) || item.priceRange || item.estimatedCost;
  const subtype = item.cuisine || item.type || item.category;
  const review = parseAiRating(item.rating);
  const hotelStars = parseHotelStarClass(item.starRating);
  const googleUrl = aiPickGoogleUrl(item, { name: pick?.label, city: pick?.city });
  const websiteUrl = aiPickWebsiteUrl(item, { name: pick?.label, city: pick?.city });
  const bookingUrl = aiPickBookingUrl(item, {
    name: pick?.label,
    city: pick?.city,
    category: pick?.category
  });
  const ratingLabel = [
    hotelStars && t('partnerAi.hotelStars', { count: hotelStars }),
    review && t('partnerAi.reviewStars', { rating: Number(review).toFixed(1) }),
    item.userRatingCount && t('partnerAi.reviewsCount', { count: item.userRatingCount })
  ].filter(Boolean).join(' · ');
  const links = [
    isHttpUrl(websiteUrl) && { href: websiteUrl, label: t('partnerAi.website'), testId: 'website' },
    isHttpUrl(googleUrl) && { href: googleUrl, label: t('partnerAi.google'), testId: 'google' },
    isHttpUrl(bookingUrl) && { href: bookingUrl, label: t('partnerAi.booking'), testId: 'book' }
  ].filter(Boolean);

  return (
    <Dialog
      open={Boolean(pick)}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ 'data-testid': 'partner-trip-ai-detail' }}
    >
      <DialogTitle sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, fontSize: 26, color: DOCUMENT.ink, pr: 2 }}>
        {pick?.label}
        {pick ? (
          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mt: 0.75, fontFamily: DOCUMENT.sans }}>
            {t(`partnerAi.${aiPickKindKey(pick.category)}`)}
            {pick.city ? ` · ${pick.city}` : ''}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent>
        {item.photoName && tripId ? (
          <Box sx={{ mb: 2 }}>
            <LazyPlacePhoto
              tripId={tripId}
              photoName={item.photoName}
              alt={pick?.label || ''}
              width="100%"
              height={180}
              usePartnerAuth={usePartnerAuth}
              fallback={<Box sx={{ width: '100%', height: 180, bgcolor: DOCUMENT.canvas }} />}
            />
          </Box>
        ) : null}
        <DetailField label={t('partnerAi.cuisine')}>{subtype && subtype !== pick?.label ? subtype : null}</DetailField>
        <DetailField label={t('partnerAi.description')}>{item.description}</DetailField>
        <DetailField label={t('partnerAi.whyRecommended')}>{item.whyRecommended}</DetailField>
        <DetailField label={t('partnerAi.estimatedCost')}>{cost}</DetailField>
        <DetailField label={t('partnerAi.duration')}>{item.duration}</DetailField>
        <DetailField label={t('partnerAi.rating')}>{ratingLabel || item.rating}</DetailField>
        <DetailField label={t('partnerAi.address')}>{item.address || item.location}</DetailField>
        <DetailField label={t('partnerAi.hours')}>{item.hours}</DetailField>
        <DetailField label={t('partnerAi.phone')}>{item.phone}</DetailField>
        <DetailField label={t('partnerAi.amenities')}>
          {Array.isArray(item.amenities) && item.amenities.length ? item.amenities.join(', ') : null}
        </DetailField>
        {links.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 0.5 }}>
            {links.map((link) => (
              <Link
                key={`${link.label}-${link.href}`}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`partner-trip-ai-detail-${link.testId}`}
                onClick={(event) => event.stopPropagation()}
                sx={{ fontSize: 14 }}
              >
                {link.label}
              </Link>
            ))}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={partnerOutlineBtnSx}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PartnerTripAIDrawer;
